<template>
<div class="dashboard-component">
  <div class="dashboard-component__content static-container">
    <div class="dashboard-component__card dashboard-component__order-card">
      <header class="dashboard-component__card-header">
        <h3 class="dashboard-component__card-header-title">Текущий заказ</h3>
        <div class="invisible flex-grow"/>
        <router-link
          :to="{ name: 'orders' }"
          class="dashboard-component__card-header-dedicated-page-link"
        >Все заказы</router-link>
      </header>
      <TransitionExpand>
        <OrderDetails
          v-if="lastOrder instanceof Object"
          :order="lastOrder"
        />
        <Preloader
          v-else-if="lastOrder === 'loading'"
          class="dashboard-component__order-loading-placeholder"
        />
        <p v-else class="p-5">Нет заказов</p>
      </TransitionExpand>
    </div>
    <!-- `&& $store.state.auth.user.customer` the user is initially set from `/auth` request, and the `customer`
    include is not included. Later it is loaded in a dedicated request. TODO make it consistent? -->
    <User
      v-if="$store.state.auth.user && $store.state.auth.user.id && $store.state.auth.user.customer"
      :user="$store.state.auth.user"
      :loyalty-profile="$store.state.auth.loyaltyProfile"
      :bonuses-levels="bonusesLevels"
      class="dashboard-component__card dashboard-component__user-card"
    />
    <div class="dashboard-component__payment-cards-and-addresses">
      <PaymentCards
        v-if="$store.getters['store/supportsOnlineCardPayments']"
        :cards="paymentCards"
        @request-reload="loadPaymentCards"
        class="dashboard-component__card dashboard-component__payment-cards-card"
      />
      <div
        class="dashboard-component__card dashboard-component__addresses-card"
      >
        <header class="dashboard-component__card-header">
          <h3 class="dashboard-component__card-header-title">Мои адреса</h3>
          <div class="invisible flex-grow"/>
          <router-link
            :to="{ name: 'addresses.list' }"
            class="dashboard-component__card-header-dedicated-page-link"
          >Все адреса</router-link>
        </header>
        <TransitionExpand>
          <AddressesList
            v-if="addresses instanceof Array"
            :addresses="addresses"
            :hide-items-buttons="true"
          />
          <Preloader
            v-else
            class="dashboard-component__addresses-loading-placeholder"
          />
        </TransitionExpand>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapState } from 'vuex';
import axios from '@/zenky';
import config from '@/config';
import TransitionExpand from '@/components/AsyncTransitionExpand';
import InjectsSeo from "@/mixins/InjectsSeo";

export default {
  mixins: [InjectsSeo],

  metaInfo() {
    return {
      title: this.getFullPageTitle('Личный кабинет'),
    }
  },

  components: {
    TransitionExpand,
    Preloader: () => import(/* webpackPreload: true */ '@/components/preloaders/Preloader'),
    OrderDetails: () => import('@/components/profile/orders/Details'),
    User: () => import(/* webpackPreload: true */ '@/components/profile/User'),
    PaymentCards: () => import('@/components/profile/PaymentCards'),
    AddressesList: () => import(/* webpackPreload: true */ '@/components/profile/addresses/List'),
  },
  created() {
    this.$store.dispatch('savedAddresses/load', this.$store.state.auth.token);
    this.$store.dispatch('orderStatuses/load'); // Need it in `OrderDetails.vue`
    this.loadLastOrder();
    this.loadBonusesLevels();
    this.loadPaymentCards();
  },
  methods: {
    async loadLastOrder() {
      const req = axios.get('/profile/orders', {
        params: {
          order_by: '-submitted_at',
          count: 1,
          with: config.includes.ordersHistoryItem,
        }
      });
      const orders = (await req).data.data;
      this.lastOrder = orders[0] || 'noOrders';
    },
    async loadBonusesLevels() {
      this.bonusesLevels = (await axios.get('/loyalty/bonuses-levels'))
        .data.data
        .sort((a, b) => a.expenses.value - b.expenses.value);
    },
    async loadPaymentCards() {
      this.paymentCards = (await axios.get('/profile/card-tokens')).data.data;
    }
  },
  computed: {
    ...mapState({
      addresses: state => state.savedAddresses.addresses,
      unsubmittedOrderId: state => state.order.order?.id,
    }),
  },
  watch: {
    unsubmittedOrderId: {
      handler(n, o) {
        // When the order has been submitted.
        if (o) this.loadLastOrder();
      },
    },
  },
  data() {
    return {
      lastOrder: 'loading',
      bonusesLevels: null,
      paymentCards: null,
    };
  },
};
</script>

<style lang="less">
@import './common';

@r: dashboard-component;
.@{r} {
  @apply p-2;
  background: @page-background;
  &__content {
    @apply flex flex-wrap items-start;
  }
  &__card:extend(.profile__card) {
    @apply m-2 flex-grow;
  }
  &__payment-cards-and-addresses {
    @apply flex flex-col; // So margins don't collapse, as it would be if there was no wrapper.
  }
  @narrow-card-max-w: 350px;
  // Setting flex-basis for cards so they don't jump around when their contents load.
  &__order-card {
    flex-basis: 500px;
    .order-details-component {
      &.expand-enter-active,
      &.expand-leave-active {
        @apply overflow-hidden;
      }
    }
  }
  &__user-card {
    max-width: @narrow-card-max-w;
    flex-basis: 230px; // Roughly the size of the bonuses level cards. DRY?
  }
  &__payment-cards-card {
    max-width: @narrow-card-max-w;
    flex-basis: 230px; // Roughly the size of the payment cards. DRY?
  }
  &__addresses-card {
    max-width: @narrow-card-max-w;
    flex-basis: 250px;
  }
  &__order-loading-placeholder {
    @order-card-content-rough-height: 25rem;
    height: @order-card-content-rough-height;
  }
  &__addresses-loading-placeholder {
    @addresses-card-content-rough-height: 12rem;
    height: @addresses-card-content-rough-height;
  }
  &__card-header:extend(.profile__card-header) {}
  &__card-header-title:extend(.profile__card-header-title) {}
  &__card-header-dedicated-page-link:extend(.profile__card-header-button) {}
}

@screen xl {
  .@{r} {
    &__user-card {
      order: -1;
    }
  }
}
</style>
